/**
 * API Keys Settings Section
 * BYOK (Bring Your Own Key) with real Fernet encryption
 */
import { useState, useEffect } from 'react';
import { useAuthStore } from '../../stores/authStore';
import { useSettingsStore } from '../../stores/settingsStore';
import { settingsApi } from '../../api/settings';
import Toggle from '../common/Toggle';

type Provider = 'anthropic' | 'openai' | 'straico';

export default function ApiKeysSettings() {
  const { user } = useAuthStore();
  const { updateSettings } = useSettingsStore();

  const [useOwnKeys, setUseOwnKeys] = useState(user?.use_own_api_keys || false);
  const [maskedKeys, setMaskedKeys] = useState<Record<Provider, string | null>>({
    anthropic: null,
    openai: null,
    straico: null,
  });

  const [showInputFor, setShowInputFor] = useState<Provider | null>(null);
  const [newKey, setNewKey] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  // Load masked keys on mount
  useEffect(() => {
    if (useOwnKeys) {
      loadMaskedKeys();
    }
  }, [useOwnKeys]);

  const loadMaskedKeys = async () => {
    try {
      const status = await settingsApi.getApiKeysStatus();
      setMaskedKeys(status.keys as Record<Provider, string | null>);
    } catch (error) {
      console.error('Failed to load API keys:', error);
    }
  };

  const handleToggleOwnKeys = async (enabled: boolean) => {
    setUseOwnKeys(enabled);
    try {
      await updateSettings({ use_own_api_keys: enabled });
      if (enabled) {
        await loadMaskedKeys();
      }
    } catch (error: any) {
      console.error('Failed to toggle API keys:', error);
    }
  };

  const handleSaveKey = async (provider: Provider) => {
    if (!newKey.trim()) {
      setSaveMessage('Please enter an API key');
      return;
    }

    setIsSaving(true);
    setSaveMessage('');

    try {
      const response = await settingsApi.saveApiKey({
        provider,
        api_key: newKey,
      });

      // Update masked keys
      setMaskedKeys((prev) => ({
        ...prev,
        [provider]: response.masked_key,
      }));

      setNewKey('');
      setShowInputFor(null);
      setSaveMessage(`${provider.charAt(0).toUpperCase() + provider.slice(1)} API key saved successfully!`);
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error: any) {
      setSaveMessage(error.response?.data?.detail?.message || 'Failed to save API key');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteKey = async (provider: Provider) => {
    if (!confirm(`Are you sure you want to delete your ${provider} API key?`)) {
      return;
    }

    try {
      await settingsApi.deleteApiKey(provider);

      // Clear masked key
      setMaskedKeys((prev) => ({
        ...prev,
        [provider]: null,
      }));

      setSaveMessage(`${provider.charAt(0).toUpperCase() + provider.slice(1)} API key deleted`);
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error: any) {
      setSaveMessage(error.message || 'Failed to delete API key');
    }
  };

  const renderProviderSection = (provider: Provider, label: string, description: string) => {
    const hasKey = maskedKeys[provider];
    const isEditing = showInputFor === provider;

    return (
      <div className="bg-dark-bg/30 border border-dark-border/50 rounded-xl p-5">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h4 className="text-sm font-semibold text-dark-text">{label}</h4>
            <p className="text-xs text-dark-text-muted mt-1">{description}</p>
          </div>

          {hasKey && !isEditing && (
            <button
              onClick={() => handleDeleteKey(provider)}
              className="text-red-400 hover:text-red-300 text-sm"
            >
              Delete
            </button>
          )}
        </div>

        {hasKey && !isEditing ? (
          <div className="flex items-center justify-between">
            <code className="text-sm text-dark-text-muted font-mono">{hasKey}</code>
            <button
              onClick={() => setShowInputFor(provider)}
              className="text-accent-blue hover:text-accent-blue/80 text-sm"
            >
              Update
            </button>
          </div>
        ) : isEditing || !hasKey ? (
          <div className="space-y-3">
            <input
              type="password"
              value={newKey}
              onChange={(e) => setNewKey(e.target.value)}
              placeholder={`Enter your ${label} API key...`}
              className="w-full bg-dark-bg border border-dark-border rounded-lg px-3 py-2 text-dark-text placeholder-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-accent-blue/50"
            />
            <div className="flex gap-2">
              <button
                onClick={() => handleSaveKey(provider)}
                disabled={isSaving}
                className="px-4 py-2 bg-accent-blue text-white text-sm rounded-lg hover:bg-accent-blue/80 disabled:opacity-50"
              >
                {isSaving ? 'Saving...' : 'Save Key'}
              </button>
              {isEditing && (
                <button
                  onClick={() => {
                    setShowInputFor(null);
                    setNewKey('');
                  }}
                  className="px-4 py-2 border border-dark-border text-dark-text text-sm rounded-lg hover:bg-dark-bg/30"
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div className="p-8 space-y-6">
      <div>
        <h3 className="text-xl font-semibold text-dark-text mb-2">API Keys</h3>
        <p className="text-sm text-dark-text-muted">
          Optionally provide your own Virtual Assistant provider API keys for more control and privacy.
        </p>
      </div>

      {/* Toggle to use own keys */}
      <div className="bg-dark-bg/30 border border-dark-border/50 rounded-xl p-5">
        <Toggle
          enabled={useOwnKeys}
          onChange={handleToggleOwnKeys}
          label="Use My Own API Keys"
          description="Enable to use your personal API keys instead of server-managed keys"
        />
      </div>

      {useOwnKeys && (
        <div className="space-y-4">
          {/* Security Notice */}
          <div className="bg-accent-blue/10 border border-accent-blue/30 rounded-xl p-4">
            <div className="flex gap-3">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-accent-blue flex-shrink-0">
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
              </svg>
              <div className="text-sm text-dark-text">
                <p className="font-medium mb-1">Your keys are encrypted</p>
                <p className="text-dark-text-muted">
                  API keys are encrypted using Fernet cipher before storage and never sent to your browser after initial save.
                </p>
              </div>
            </div>
          </div>

          {/* Provider Sections */}
          {renderProviderSection(
            'anthropic',
            'Anthropic (Claude)',
            'API key for Claude models (starts with sk-ant-)'
          )}

          {renderProviderSection(
            'openai',
            'OpenAI',
            'API key for GPT models (starts with sk-)'
          )}

          {renderProviderSection(
            'straico',
            'Straico',
            'API key for Straico unified platform'
          )}
        </div>
      )}

      {saveMessage && (
        <div className={`text-sm ${saveMessage.includes('success') || saveMessage.includes('deleted') ? 'text-green-400' : 'text-red-400'}`}>
          {saveMessage}
        </div>
      )}
    </div>
  );
}
